import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import {
    createUseStyles,
    useTheme,
    Button,
    Icon,
    Markdown,
    Progress,
    Text,
} from '@Sage/hoi-poi-ui';
import { getLocaleFormat, truncate } from 'utils/numbers';
import { useWeb5Context } from 'lib/Web5Context';

import styles from '../styles/UsageCard';
const useStyles = createUseStyles(styles, 'UsageCard');

const LOCALE_FORMAT = {
    useGrouping: true,
    minimumFractionDigits: 0,
};

const UsageCard = memo(
    ({
        title,
        icon,
        disabled,
        onHover,
        progress,
        progressCustomFormat,
        locale,
        info,
        itemCode,
        linkCallback,
    }) => {
        const classes = useStyles();
        const theme = useTheme();
        const { t } = useWeb5Context();

        const percentage = useMemo(() => {
            if (!progress || isNaN(progress.value) || isNaN(progress.max)) {
                return null;
            }

            const { value, max } = progress;
            const percentage =
                value === 0 && max === 0 ? 0 : truncate(((value / max) * 100).toFixed(1), 1) || 0;

            return `${getLocaleFormat(percentage, locale, LOCALE_FORMAT)}%`;
        }, [locale, progress]);

        const rootProps = useMemo(
            () => ({
                className: classNames(classes.root, { [classes.hasInfo]: !!info }),
                onMouseEnter: () => onHover && onHover(itemCode),
            }),
            [classes.hasInfo, classes.root, info, onHover, itemCode],
        );

        const iconProps = useMemo(
            () => ({
                name: icon,
                className: classes.icon,
                color: disabled ? theme.colors.utility.textDisabled : theme.colors.actionMajor[500],
            }),
            [classes.icon, disabled, icon, theme],
        );

        const progressProps = useMemo(
            () => ({
                ...progress,
                animation: {
                    animationDuration: '1s',
                    animationTimingFunction: 'cubic-bezier(.6, 0, .5, .9)',
                },
                className: classes.progress,
            }),
            [classes.progress, progress],
        );

        const text = useMemo(() => {
            switch (true) {
                case !!progress:
                    let value = getLocaleFormat(progress?.value, locale, LOCALE_FORMAT);
                    let max = getLocaleFormat(progress?.max, locale, LOCALE_FORMAT);
                    if (progressCustomFormat) {
                        value = progressCustomFormat(value);
                        max = progressCustomFormat(max);
                    }
                    return t('label_in_use_from_where', [value, max]);
                case disabled:
                    return t('label_feature_no_contracted');
                default:
                    break;
            }
        }, [disabled, locale, progress, progressCustomFormat, t]);

        const hoverContent = useMemo(() => {
            if (!info || typeof info !== 'object') return null;
            const { text, cta } = info;
            return (
                <div className={classes.hoverContent}>
                    {text && (
                        <Markdown
                            content={text}
                            overrides={{
                                a: { target: '_blank', type: 'caption' },
                                p: { color: theme.colors.textLight.secondary, type: 'caption' },
                            }}
                            className={classes.hoverContentMarkdown}
                            linkCallback={linkCallback}
                        />
                    )}
                    {cta.length && (
                        <div className={classes.hoverContentCTA}>
                            {cta.map((button, index) => (
                                <Button
                                    type={button.type}
                                    onClick={button.onClick}
                                    size="small"
                                    key={index}
                                >
                                    {button.text}
                                </Button>
                            ))}
                        </div>
                    )}
                </div>
            );
        }, [
            classes.hoverContent,
            classes.hoverContentCTA,
            classes.hoverContentMarkdown,
            info,
            linkCallback,
            theme,
        ]);

        return (
            <div {...rootProps}>
                <div className={classes.header}>
                    <Icon {...iconProps} />
                    <Text type="subtitle" medium color={theme.colors.textLight.primary}>
                        {title}
                    </Text>
                </div>
                <div className={classes.footer}>
                    <Text type="caption" color={theme.colors.textLight.secondary}>
                        {text}
                    </Text>
                    {percentage && (
                        <Text
                            type="caption"
                            color={theme.colors.textLight.primary}
                            className={classes.percentage}
                        >
                            {percentage}
                        </Text>
                    )}
                    <Progress {...progressProps} />
                </div>
                {hoverContent}
            </div>
        );
    },
);

export default UsageCard;
